import React, { createContext, useState, useContext, useEffect } from 'react';

// Same breakpoint as in use-mobile.tsx
const MOBILE_BREAKPOINT = 768;

interface SidebarContextType {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  showMobileSidebar: boolean;
  setShowMobileSidebar: (value: boolean) => void;
  isMobile: boolean;
  linkClicked: boolean;
  setLinkClicked: (value: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [linkClicked, setLinkClicked] = useState<boolean>(false);

  // Check screen size on mount and when window resizes
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      console.log('SidebarContext - Checking if mobile:', mobile, 'window width:', window.innerWidth);
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      }
    };

    // Initial check
    checkScreenSize();

    // Add event listener
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Effect to force sidebar collapse when a link is clicked on mobile
  useEffect(() => {
    if (linkClicked) {
      console.log('Link was clicked, handling sidebar state');
      if (isMobile) {
        setShowMobileSidebar(false);
      }
      
      const timer = setTimeout(() => {
        setLinkClicked(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [linkClicked, isMobile]);

  return (
    <SidebarContext.Provider
      value={{
        collapsed,
        setCollapsed,
        showMobileSidebar,
        setShowMobileSidebar,
        isMobile,
        linkClicked,
        setLinkClicked,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export default SidebarContext; 